import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useMe as libUseMe,
  useLogin as libUseLogin,
  useLogout as libUseLogout,
  useChangePassword as libUseChangePassword,
} from "@marekl/adminer";
import { apiGet, apiPost, apiPatch, apiDelete, ApiError } from "../lib/api";
import type { Dayjs } from "dayjs";
import type { ID } from "../lib/id";

// SystemRole mirrors models.SystemRole on the backend. The
// team-scoped values team_leader / coach / assistant / athlete are
// virtual — derived by rbac.Load from TeamLeadership and
// TeamMembership respectively — but the frontend sees them in the
// uniform Assignment shape.
export type SystemRole =
  | "admin"
  | "members_admin"
  | "members_viewer"
  | "competitions_admin"
  | "competitions_viewer"
  | "global_viewer"
  | "judge"
  | "federation_admin"
  | "competition_organizer"
  | "custom_pages_manager"
  | "notices_manager"
  | "team_leader"
  | "coach"
  | "assistant"
  | "athlete";

export type RoleScopeType = "global" | "federation" | "team" | "competition";

export interface Assignment {
  id?: ID;
  role: SystemRole;
  scope_type: RoleScopeType;
  scope_id?: ID | null;
  /** The team's, federation's or competition's name, resolved server-side. */
  scope_name?: string;
  virtual?: boolean;
}

export interface Impersonator {
  id: ID;
  email: string;
  display_name: string;
}

export interface User {
  id: ID;
  email: string;
  display_name: string;
  person_id?: ID | null;
  active: boolean;
  password_locked: boolean;
  last_login_at?: Dayjs;
  assignments: Assignment[];
  impersonator?: Impersonator | null;
}

// Login credentials use the lib-canonical shape {identifier, password}.
// We carry the user's email in the `identifier` slot; the REST auth
// adapter still posts it under the `username` wire field (its default
// identifierField), so the backend contract is unchanged.
export type { IdentifierCredentials } from "@marekl/adminer";

/** Fetches the currently authenticated user. Returns null if not logged in. */
export function useMe() {
  return libUseMe<User>();
}

/** Logs in and primes the me query cache with the returned user. */
export function useLogin() {
  return libUseLogin<User>();
}

/** Logs out and clears all cached query data. */
export function useLogout() {
  return libUseLogout();
}

/** Re-export of the lib's change-password mutation; payload is `{ oldPassword, newPassword }`. */
export const useChangePassword = libUseChangePassword;

interface UsersListResponse {
  users: User[];
}

/** Fetches all users (admin only). */
export function useUsers() {
  return useQuery<User[]>({
    queryKey: ["users"],
    queryFn: async () => {
      const res = await apiGet<UsersListResponse>("/users");
      return res.users;
    },
  });
}

// Safe-field PATCH; role assignments are managed via a separate
// (future) endpoint to keep audit trails clear.
export interface UpdateUserPayload {
  display_name?: string;
  email?: string;
  active?: boolean;
  password_locked?: boolean;
}

export function useUpdateUser(id: ID) {
  const qc = useQueryClient();
  return useMutation<User, ApiError, UpdateUserPayload>({
    mutationFn: (payload) => apiPatch<User>(`/users/${id}`, payload),
    // Rendered in EditUserModal's error slot, its only caller.
    meta: { suppressErrorToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

/**
 * The admin no longer chooses a password: the account is made unusable and
 * its owner sets one from a mailed link. set_password_url comes back ONLY
 * when the server has no letterman configured, in which case the admin has
 * to relay it by hand.
 */
export interface ResetPasswordResult {
  status: string;
  set_password_url?: string;
}

export function useResetUserPassword(id: ID) {
  const qc = useQueryClient();
  return useMutation<ResetPasswordResult, ApiError, void>({
    mutationFn: () => apiPost<ResetPasswordResult>(`/users/${id}/reset-password`, {}),
    // Rendered in ResetPasswordModal's error slot, its only caller.
    meta: { suppressErrorToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export interface CreateUserPayload {
  email: string;
  display_name: string;
  active?: boolean;
  password_locked?: boolean;
}

/** A User plus, only with no mail configured, the link to relay. */
export type CreateUserResult = User & { set_password_url?: string };

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<CreateUserResult, ApiError, CreateUserPayload>({
    mutationFn: (payload) => apiPost<CreateUserResult>("/users", payload),
    // Rendered in CreateUserModal's error slot, its only caller.
    meta: { suppressErrorToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

// ---- Role assignments (storable roles only) ----

export interface AssignRolePayload {
  role: SystemRole;
  scope_type: RoleScopeType;
  scope_id?: ID | null;
}

export function useAssignRole(userId: ID) {
  const qc = useQueryClient();
  return useMutation<User, ApiError, AssignRolePayload>({
    mutationFn: (payload) =>
      apiPost<User>(`/users/${userId}/role-assignments`, payload),
    // Reported by the caller: the revoke through the confirm dialog's error
    // slot, the assign through its own toast. The global handler fires IN
    // ADDITION to a mutation's onError, so without this it said it twice.
    meta: { suppressErrorToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useRevokeRole(userId: ID) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ID>({
    mutationFn: (assignmentId) =>
      apiDelete<void>(`/users/${userId}/role-assignments/${assignmentId}`),
    // Reported by the caller: the revoke through the confirm dialog's error
    // slot, the assign through its own toast. The global handler fires IN
    // ADDITION to a mutation's onError, so without this it said it twice.
    meta: { suppressErrorToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

/** Revoke variant taking both ids as variables — for surfaces where the
 * target user differs per row (e.g. a competition's organizer list). */
export function useRevokeRoleAssignment() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { userId: ID; assignmentId: ID }>({
    mutationFn: ({ userId, assignmentId }) =>
      apiDelete<void>(`/users/${userId}/role-assignments/${assignmentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

// ---- Impersonation ----

/** Starts an impersonation session of the given target user. On success
 * the browser is hard-redirected to "/" to fully reset React Query caches
 * and pick up the new session cookies. */
export function useStartImpersonation() {
  return useMutation<void, ApiError, ID>({
    mutationFn: (targetUserId) =>
      apiPost<void>(`/users/${targetUserId}/impersonate`, {}),
    // Reported inside the confirm dialog, which stays open on a refusal.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      window.location.assign("/");
    },
  });
}

/** Ends the current impersonation session and restores the original admin
 * session. Hard-redirects to /users on success. */
export function useStopImpersonation() {
  return useMutation<void, ApiError, void>({
    mutationFn: () => apiPost<void>(`/auth/stop-impersonation`, {}),
    onSuccess: () => {
      window.location.assign("/users");
    },
  });
}

// ---- RBAC helpers (frontend mirror of internal/service/rbac) ----

export function hasGlobalRole(user: User | null | undefined, role: SystemRole): boolean {
  if (!user) return false;
  return user.assignments.some((a) => a.role === role && a.scope_type === "global");
}

export function hasAnyGlobalRole(user: User | null | undefined, ...roles: SystemRole[]): boolean {
  return roles.some((r) => hasGlobalRole(user, r));
}

export function hasAnyAssignmentRole(user: User | null | undefined, ...roles: SystemRole[]): boolean {
  if (!user) return false;
  return user.assignments.some((a) => roles.includes(a.role));
}

export function hasTeamRole(
  user: User | null | undefined,
  teamId: ID,
  role: SystemRole,
): boolean {
  if (!user) return false;
  return user.assignments.some(
    (a) => a.role === role && a.scope_type === "team" && a.scope_id === teamId,
  );
}

export function hasFederationRole(
  user: User | null | undefined,
  fedId: ID,
  role: SystemRole,
): boolean {
  if (!user) return false;
  return user.assignments.some(
    (a) => a.role === role && a.scope_type === "federation" && a.scope_id === fedId,
  );
}

/**
 * True when the user administers any federation at all. The team pages are
 * open to a Správce federace, whose scope is one federation rather than a
 * global role, so „may this user open Týmy" cannot be a global-role check.
 * What they then SEE is scoped by the server.
 */
export function administersAnyFederation(user: User | null | undefined): boolean {
  if (!user) return false;
  return user.assignments.some(
    (a) => a.role === "federation_admin" && a.scope_type === "federation",
  );
}

export function hasCompetitionRole(
  user: User | null | undefined,
  competitionId: ID,
  role: SystemRole,
): boolean {
  if (!user) return false;
  return user.assignments.some(
    (a) => a.role === role && a.scope_type === "competition" && a.scope_id === competitionId,
  );
}

/** Mirror of rbac.CanAdministerCompetitions: the competition office as a
 * whole (create / delete competitions and series, every draft, rule sets).
 * Admin or competitions_admin; members_admin is deliberately not one. */
export function canAdministerCompetitions(user: User | null | undefined): boolean {
  return hasAnyGlobalRole(user, "admin", "competitions_admin");
}

/** Mirror of rbac.CanManageCompetition: canAdministerCompetitions, or the
 * competition's organizer. */
export function canManageCompetition(
  user: User | null | undefined,
  competitionId: ID,
): boolean {
  return (
    canAdministerCompetitions(user) ||
    hasCompetitionRole(user, competitionId, "competition_organizer")
  );
}

/** Mirror of rbac.CanReadAllCompetitions: who may READ every competition,
 * drafts included — the competition office and the read-only
 * competitions_viewer. Never gate a write on it. */
export function canReadAllCompetitions(user: User | null | undefined): boolean {
  return hasAnyGlobalRole(
    user,
    "admin",
    "competitions_admin",
    "competitions_viewer",
    "global_viewer",
  );
}

/** Mirror of rbac.CanViewCompetition: whoever manages the competition, plus
 * the read-only competitions_viewer. Opens the competition shell. */
export function canViewCompetition(user: User | null | undefined, competitionId: ID): boolean {
  return (
    canManageCompetition(user, competitionId) ||
    hasAnyGlobalRole(user, "competitions_viewer", "global_viewer")
  );
}

/** True when the user reaches a competition only as the read-only
 * competitions_viewer: every write on its pages is hidden. */
export function readsCompetitionOnly(user: User | null | undefined, competitionId: ID): boolean {
  return (
    hasAnyGlobalRole(user, "competitions_viewer", "global_viewer") &&
    !canManageCompetition(user, competitionId)
  );
}

/** True when the user reads the rule sets without writing them (the
 * competitions_viewer, or anybody else who reaches the pages). False while
 * the user is still loading, so the office never flashes read-only. */
export function readsRuleSetsOnly(user: User | null | undefined): boolean {
  return !!user && !canAdministerCompetitions(user);
}

/** The team fields rbac.CanManageTeam reads. */
export interface TeamManageRef {
  id: ID;
  federation_id?: ID | null;
  federation_is_primary: boolean;
  federation_is_umbrella?: boolean;
  status: string;
}

/** Mirror of rbac.AgreesForTeam: only the team's leader agrees to the
 * season documents on its behalf. An administrator registers the club and
 * leaves the documents pending for the leader. */
export function agreesForTeam(user: User | null | undefined, teamId: ID): boolean {
  return hasTeamRole(user, teamId, "team_leader");
}

/** Mirror of rbac.CanAdministerTeam: admin; the team's federation_admin;
 * members_admin on a team central staff run (ČNSMS, the umbrella, or none).
 * Never the leader: approving, suspending and renaming are the federation's. */
export function canAdministerTeam(user: User | null | undefined, team: TeamManageRef): boolean {
  if (hasGlobalRole(user, "admin")) return true;
  if (team.federation_id && hasFederationRole(user, team.federation_id, "federation_admin")) {
    return true;
  }
  const central = team.federation_is_primary || !!team.federation_is_umbrella || !team.federation_id;
  return central && hasGlobalRole(user, "members_admin");
}

/** Mirror of rbac.CanManageTeam: whoever administers the team
 * (canAdministerTeam), plus the leader of an ACTIVE team. */
export function canManageTeam(user: User | null | undefined, team: TeamManageRef): boolean {
  if (canAdministerTeam(user, team)) return true;
  return team.status === "active" && hasTeamRole(user, team.id, "team_leader");
}

/** True when the user reaches a team only as the read-only members_viewer
 * (rbac.CanViewTeam without rbac.CanManageTeam): every write is hidden. */
export function readsTeamOnly(user: User | null | undefined, team: TeamManageRef | undefined): boolean {
  return (
    !!team &&
    hasAnyGlobalRole(user, "members_viewer", "global_viewer") &&
    !canManageTeam(user, team)
  );
}

/** Who may file a přihláška for any club from the organizer's side
 * („Přidat přihlášku"). The wizard needs three things at once: managing the
 * competition, reading the club's roster, and adding a nečlen to it
 * (rbac.CanManageTeam). Since the two offices split, only admin holds all
 * three alone; a competitions_admin needs members_admin beside it. */
export function canFileEntryForAnyTeam(user: User | null | undefined): boolean {
  return canAdministerCompetitions(user) && hasAnyGlobalRole(user, "admin", "members_admin");
}

/** Mirror of rbac.CanReadAllMembers: who may READ every team and person —
 * admin and members_admin, who also write, and the read-only
 * members_viewer. Never gate a write on it. */
export function canReadAllMembers(user: User | null | undefined): boolean {
  return hasAnyGlobalRole(user, "admin", "members_admin", "members_viewer", "global_viewer");
}

/** Mirror of rbac.ReadsEverything: the administrator's own screens (users,
 * e-mails, access, federations) are admin's and the read-only
 * global_viewer's. Never gate a write on it. */
export function readsEverything(user: User | null | undefined): boolean {
  return hasAnyGlobalRole(user, "admin", "global_viewer");
}

/** True when the user reaches a screen only as the read-only global_viewer:
 * none of `writers` (the roles that write there) is theirs, so every write
 * on it is hidden. False while the user loads, so a writer never flashes
 * read-only. */
export function readsOnlyAsGlobalViewer(
  user: User | null | undefined,
  ...writers: SystemRole[]
): boolean {
  return hasGlobalRole(user, "global_viewer") && !hasAnyGlobalRole(user, "admin", ...writers);
}

export function isAdmin(user: User | null | undefined): boolean {
  return hasGlobalRole(user, "admin");
}
